import type { FinancialCarrier } from '@/const/Financial'
import type { FinancialFilePreview } from '@/types/Components/FinancialFilePreview'
import {
  FINANCIAL_FILE_MAX_BYTES,
  FINANCIAL_FILE_MAX_COLUMNS,
  FINANCIAL_FILE_MAX_ROWS,
  FINANCIAL_PREVIEW_ROWS,
} from '@/const/Financial'
import { financialCarriersIn, readFinancialWorkbook } from '@/services/FinancialWorkbookPreview'

// This only inspects file structure. Prices and identifiers remain unmodified strings.
export const parseFinancialFile = (source: string, allRows = false): FinancialFilePreview => {
  const text = source.replace(/^\uFEFF/, '')
  if (!text.trim()) throw new Error('The file is empty. Choose a file with a header and invoice rows.')
  if (text.includes('\0')) throw new Error('Use a UTF-8 CSV or TSV file. This file contains unsupported characters.')

  const separators: FinancialFilePreview['delimiter'][] = [',', ';', '\t']
  const counts = [0, 0, 0]
  let insideQuotes = false
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    if (character === '"') {
      if (insideQuotes && text[index + 1] === '"') index += 1
      else insideQuotes = !insideQuotes
    } else if (!insideQuotes) {
      if (character === '\n' || character === '\r') break
      const separatorIndex = separators.indexOf(character as FinancialFilePreview['delimiter'])
      if (separatorIndex >= 0) counts[separatorIndex] += 1
    }
  }
  const mostSeparators = Math.max(...counts)
  if (!mostSeparators || counts.filter((count) => count === mostSeparators).length !== 1) {
    throw new Error('The column separator is unclear. Use comma, semicolon or tab, with a header on the first row.')
  }
  const delimiter = separators[counts.indexOf(mostSeparators)]
  let headers: string[] = []
  const rows: string[][] = []
  let rowCount = 0
  let record: string[] = []
  let field = ''
  let quoted = false
  let closedQuote = false

  const finishField = (): void => {
    record.push(field)
    if (record.length > FINANCIAL_FILE_MAX_COLUMNS) throw new Error('A file can contain at most 100 columns.')
    field = ''
    closedQuote = false
  }
  const finishRecord = (): void => {
    finishField()
    // Spreadsheet programs export formatted empty rows as ;;;; — skip them like empty XLSX rows.
    if (record.every((value) => value.trim() === '')) {
      record = []
      return
    }
    if (!headers.length) {
      headers = record.map((header) => header.trim())
      if (
        headers.some((header) => !header) ||
        new Set(headers.map((header) => header.toLowerCase())).size !== headers.length
      ) {
        throw new Error('Each column needs a unique, non-empty header.')
      }
    } else {
      rowCount += 1
      if (record.length !== headers.length) {
        throw new Error(
          `Invoice row ${rowCount} has ${record.length} columns; the header has ${headers.length}. Check the file and try again.`,
        )
      }
      if (rowCount > FINANCIAL_FILE_MAX_ROWS)
        throw new Error('A file can contain at most 100,000 invoice rows. Contact the PacSort team; do not edit the original.')
      if (allRows || rows.length < FINANCIAL_PREVIEW_ROWS) rows.push(record)
    }
    record = []
  }

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    if (quoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          field += '"'
          index += 1
        } else {
          quoted = false
          closedQuote = true
        }
      } else field += character
    } else if (character === delimiter) {
      finishField()
    } else if (character === '\r' || character === '\n') {
      finishRecord()
      if (character === '\r' && text[index + 1] === '\n') index += 1
    } else if (character === '"' && field === '' && !closedQuote) {
      quoted = true
    } else {
      if (closedQuote || character === '"')
        throw new Error('The file contains an invalid quoted field. Export it as CSV again and retry.')
      field += character
    }
  }
  if (quoted) throw new Error('The file ends inside a quoted field. Check that the export is complete.')
  if (field !== '' || record.length || closedQuote) finishRecord()
  if (!rowCount) throw new Error('The file has a header but no invoice rows.')
  return { headers, rows, rowCount, delimiter }
}

export const readFinancialFile = (
  file: File,
  carrier?: FinancialCarrier,
  allRows = false,
): Promise<FinancialFilePreview> => {
  if (!/\.(csv|tsv|xlsx)$/i.test(file.name))
    return Promise.reject(new Error('Choose an XLSX, CSV or TSV file. Legacy XLS and PDF files are not supported.'))
  const isWorkbook = /\.xlsx$/i.test(file.name)
  if (file.size > FINANCIAL_FILE_MAX_BYTES)
    return Promise.reject(
      new Error('The file is larger than 20 MB. Contact the PacSort team; do not split or edit the original invoice.'),
    )
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = (): void => reject(new Error('The file could not be read. Choose it again and retry.'))
    reader.onabort = (): void => reject(new Error('Reading was interrupted. Choose the file again.'))
    reader.onload = (): void => {
      if (isWorkbook) {
        readFinancialWorkbook(reader.result as ArrayBuffer, carrier, allRows).then(resolve, reject)
        return
      }
      try {
        const text = new TextDecoder('utf-8', { fatal: true }).decode(reader.result as ArrayBuffer)
        const parsed = parseFinancialFile(text, allRows)
        const found = carrier ? [carrier] : financialCarriersIn([parsed.headers])
        resolve({ ...parsed, carrier: found.length === 1 ? found[0] : undefined })
      } catch (error) {
        reject(
          error instanceof TypeError
            ? new Error('Use a UTF-8 CSV or TSV export. The file encoding is not supported.')
            : error,
        )
      }
    }
    reader.readAsArrayBuffer(file)
  })
}
